import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  ModalClose,
  ModalDialog,
  Table,
  Typography,
  Sheet,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import {
  Sku,
  useInspectionCreateDraftWithSkUsMutation,
} from "../../../__generated__/types-and-hooks";
import { useCallback, useEffect, useState } from "react";
import { useTeam } from "../TeamLayout";
import { useNotify } from "../../../hooks/useNotify";
import QNumberInput from "../../../x-components/QNumberInput";
import InspectionRef from "../../../components/InspectionRef";
import { PSIValue } from "../../../components/inspections/PSI";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

export interface BookInspectionModalProps {
  open: boolean;
  skus: Sku[];
  onClose: () => void;
}

export default function BookInspectionModal({
  open,
  skus: skuInput,
  onClose,
}: BookInspectionModalProps) {
  const [skus, setSkus] = useState(skuInput);
  const t = useTranslation();
  const teamCtx = useTeam();
  const notify = useNotify();
  const [newInspection, setNewInspection] = useState<{
    uniqueId: string;
  } | null>(null);

  useEffect(() => {
    setSkus(skuInput);
  }, [skuInput]);

  const handleRemove = useCallback(
    (index: number) => {
      skus.splice(index, 1);
      setSkus([...skus]);
    },
    [skus]
  );

  useEffect(() => {
    setNewInspection(null);
  }, [skus]);

  const [createDraftInspection, { loading }] =
    useInspectionCreateDraftWithSkUsMutation({});

  const handleSave = useCallback(() => {
    createDraftInspection({
      variables: {
        draft: {
          brandId: teamCtx.team.id,
          serviceType: PSIValue,
          inspectionLots: skus.map((sku) => ({
            skuUniqueId: sku.uniqueId,
            sku: sku.sku,
            lotSize: 100,
            inspectionLevel: "GI",
          })),
        },
      },
      onCompleted(inspection) {
        setNewInspection(inspection.inspection_create_draft);
        notify({
          type: "success",
          msg: `Inspection ${inspection.inspection_create_draft.uniqueId} booked`,
        });
      },
    });
  }, [skus, teamCtx, notify, createDraftInspection]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("Create Draft Inspection")}</DialogTitle>
        <DialogContent>
          {!newInspection && (
            <>
              <Table
                borderAxis="xBetween"
                size="sm"
                stickyHeader
                sx={{
                  my: 2,
                  "& thead td": {
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "xs",
                    color: "neutral.600",
                  },
                }}
              >
                <thead>
                  <tr>
                    <td style={{ width: "100%" }}>{t("SKU")}</td>
                    <td style={{ width: 120 }}>{t("Quantity")}</td>
                    <td style={{ width: 64 }} />
                  </tr>
                </thead>
                <tbody>
                  {skus.map((sku, index) => (
                    <tr key={sku.id}>
                      <td style={{ width: "100%" }}>
                        <Typography level="body-sm">{sku.sku}</Typography>
                      </td>
                      <td style={{ width: 120 }}>
                        <QNumberInput
                          val={100}
                          min={1}
                          max={10000}
                          update={async () => null}
                        />
                      </td>
                      <td style={{ width: 64 }}>
                        <IconButton
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemove(index)}
                        >
                          <DeleteOutlineOutlinedIcon />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button
                disabled={loading}
                loading={loading}
                fullWidth
                onClick={handleSave}
              >
                {t("Book Inspection")}
              </Button>
            </>
          )}
          {newInspection && (
            <Sheet
              variant="soft"
              color="neutral"
              sx={{
                borderRadius: "md",
                p: 3,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                mt: 2,
              }}
            >
              <Typography level="body-md">
                {t("A new inspection has been booked")}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ wordBreak: "break-all" }}
              >
                <InspectionRef
                  inspectionRef={newInspection.uniqueId}
                  teamSlug={teamCtx.team.slug}
                />
              </Box>
              <Button color="primary" fullWidth onClick={onClose}>
                {t("Close")}
              </Button>
            </Sheet>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
